import * as React from 'react';

import { routes } from 'defs';
import i18n from 'helper/i18n';
import mapBackendConfig from 'helper/mapBackendConfig';

import Checkbox from 'components/atoms/Checkbox';
import FieldInput from 'components/atoms/FieldInput';
import Link from 'components/atoms/Link';
import Spacer from 'components/helper/Spacer';
import Agreement from 'components/molecules/Agreement';
import ButtonBar from 'components/molecules/ButtonBar';
import ElementList from 'components/molecules/ElementList';

import { isNativePlatformAndroid, isNativePlatformIOS } from 'components/helper/Platform';
import withValidation from 'components/hoc/withValidation';
import { ButtonBarProps } from 'components/molecules/ButtonBar';
import { AnamnesisStepResolver } from 'components/organisms/Anamnesis';
import sanitize from 'helper/sanitizer';
import { VALIDATE_IS_NOT_ALLOWED, VALIDATE_IS_NOT_RECOMMENDED, VALIDATE_IS_PENDING } from 'helper/validator';
import heightValidatorFactory from 'helper/validator/height';
import { actionCreators } from 'store/profile';

const ControlledInput = withValidation(
	FieldInput,
	heightValidatorFactory as any,
	[sanitize.maxLength(3), sanitize.stripInvalidIntegerChars],
	{
		shouldValidateInitial: true,
	}
);

type DetermineHeightProps = {
	actionButtonsActive?: ButtonBarProps;
	actionButtonsInactive?: ButtonBarProps;
	isUpdatingAnamnesis: boolean;
	size?: number | string;
	onNavigationClick: (targetUrl: { href: string }) => void /* parent */;
	onStepCompleted: (resolver: AnamnesisStepResolver) => void /* parent */;
	doSaveHeight?: typeof actionCreators.doSaveHeight /* actionCreator */;
};

type DetermineHeightState = {
	inputValue: string | number;
	inputError: string | null;
	hasAcceptedWarning: boolean;
};

export default class DetermineHeight extends React.Component<DetermineHeightProps, DetermineHeightState> {
	public static displayName = 'AnamnesisDetermineHeight';

	constructor(props: DetermineHeightProps) {
		super(props);

		this.state = {
			inputValue: props.size || '',
			inputError: null,
			hasAcceptedWarning: false,
		};
	}

	public componentDidUpdate(prevProps: DetermineHeightProps) {
		if (this.props.size !== prevProps.size) {
			this.setState({
				inputValue: this.props.size!,
			});
		}
	}

	public isRecommended() {
		return this.state.inputError !== VALIDATE_IS_NOT_RECOMMENDED;
	}

	public isValidSubmission() {
		const { inputValue, inputError, hasAcceptedWarning } = this.state;

		const inacceptableValues = [VALIDATE_IS_NOT_ALLOWED, VALIDATE_IS_PENDING];

		if (inputValue === '') {
			return false;
		}

		if (inacceptableValues.includes(inputError!)) {
			return false;
		}

		if (inputError === VALIDATE_IS_NOT_RECOMMENDED && !hasAcceptedWarning) {
			return false;
		}

		return true;
	}

	public handleInputChange = (value: string | number) => {
		this.setState({
			inputValue: value || '',
			inputError: VALIDATE_IS_PENDING,
		});
	};

	public handleInputValidation = (error?: string) => {
		this.setState({
			inputError: error!,
		});
	};

	public handleButtonClickComplete = () => {
		const { onStepCompleted, doSaveHeight } = this.props;

		const { inputValue } = this.state;

		onStepCompleted.call(null, {
			waitFor: 'savedHeight',
			actionCreator: doSaveHeight,
			nextRoute: routes.anamnesis.determineWeight,
			data: {
				height: parseFloat(inputValue as string),
				acceptedHeightWarning: this.state.hasAcceptedWarning,
			},
		});
	};

	public handleButtonClicks = (type: string) => {
		const { onNavigationClick } = this.props;

		if (type === 'completed') {
			this.handleButtonClickComplete();
			return;
		}

		return onNavigationClick.call(this, routes.anamnesis.determineAge);
	};

	public handleAcceptWarning = (accepted: boolean) => {
		this.setState({
			hasAcceptedWarning: !!accepted,
		});
	};

	get enableMobileDateKeyboard(): boolean {
		return isNativePlatformIOS();
	}

	public renderWarning() {
		return (
			<Agreement
				title={i18n('react.impulsCoach.components.anamnesis.determineHeight.exceedsRecommendedTitle')}
				text={i18n('react.impulsCoach.components.anamnesis.determineHeight.exceedsRecommendedText')}
			>
				<ElementList>
					<Checkbox
						tag="li"
						id="anamnesis-size-accept"
						name="anamnesis-size-accept"
						label={i18n(
							'react.impulsCoach.components.anamnesis.determineHeight.exceedsRecommendedAcceptText'
						)}
						onChange={this.handleAcceptWarning}
						modifier="white"
					/>
				</ElementList>
			</Agreement>
		);
	}

	public renderLeaveCoach() {
		return (
			<Link
				title={i18n('react.impulsCoach.components.anamnesis.leaveCoachCaption')}
				iconRight={'arrow-right'}
				externalHref={mapBackendConfig.env.migrosImpulsCoachLandingPage.href}
			/>
		);
	}

	public renderActiveButtons() {
		return (
			<div className="o-anamnesis__button-bar">
				<ButtonBar
					{...(this.props.actionButtonsActive as ButtonBarProps)}
					loading={this.props.isUpdatingAnamnesis}
					handleButtonClicks={this.handleButtonClicks}
				/>
			</div>
		);
	}

	public renderInactiveButtons() {
		return (
			<div className="o-anamnesis__button-bar">
				<ButtonBar
					{...(this.props.actionButtonsInactive as ButtonBarProps)}
					loading={this.props.isUpdatingAnamnesis}
					handleButtonClicks={this.handleButtonClicks}
				/>
			</div>
		);
	}

	public render() {
		const { inputValue, inputError } = this.state;

		return (
			<div>
				<Spacer size="m" />

				<ControlledInput
					id="size"
					name="size"
					mobileFieldType={this.enableMobileDateKeyboard ? 'integer' : undefined}
					placeholder={i18n('react.impulsCoach.components.anamnesis.determineHeight.placeholderSize')}
					value={inputValue.toString()}
					unit={i18n('react.impulsCoach.components.anamnesis.determineHeight.unit')}
					onChange={this.handleInputChange as any}
					onValidationDone={this.handleInputValidation}
					disabled={this.props.isUpdatingAnamnesis}
					autoComplete={!isNativePlatformAndroid()}
				/>

				<Spacer size="l" />

				{!this.isRecommended() ? this.renderWarning() : null}
				{inputError === VALIDATE_IS_NOT_ALLOWED ? this.renderLeaveCoach() : null}

				{this.isValidSubmission() ? this.renderActiveButtons() : this.renderInactiveButtons()}
			</div>
		);
	}
}
